import { maybe, Maybe, rd, RemoteData } from '@passionware/monads';
import { AppScope, ResolvedAppScope } from 'v5/core/app-scope/appScope.types';
import {
  EnterpriseTeam,
  EnterpriseWorkspace
} from 'v5/api/enterprise/enterprise.api';
import { EnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';

function resolveWorkspaceScope(
  organizationId: string,
  workspaceSlug: string,
  workspaceRd: RemoteData<Maybe<EnterpriseWorkspace>>
): RemoteData<ResolvedAppScope> {
  return rd.map(workspaceRd, ws => {
    if (maybe.isAbsent(ws)) {
      throw new Error(`Workspace "${workspaceSlug}" not found`);
    }
    return {
      level: 'workspace',
      organizationId,
      workspaceId: ws.id,
      workspaceSlug: ws.slug
    };
  });
}

function resolveTeamScope(
  organizationId: string,
  workspaceSlug: string,
  teamSlug: string,
  workspaceRd: RemoteData<Maybe<EnterpriseWorkspace>>,
  teamRd: RemoteData<Maybe<EnterpriseTeam>>
): RemoteData<ResolvedAppScope> {
  return rd.map(
    rd.combine({ ws: workspaceRd, team: teamRd }),
    ({ ws, team }) => {
      if (maybe.isAbsent(ws)) {
        throw new Error(`Workspace "${workspaceSlug}" not found`);
      }
      if (maybe.isAbsent(team)) {
        throw new Error(`Team "${teamSlug}" not found`);
      }
      return {
        level: 'team',
        organizationId,
        workspaceId: ws.id,
        workspaceSlug: ws.slug,
        teamId: team.id,
        teamSlug: team.slug
      };
    }
  );
}

export function useResolvedAppScope(
  slugScope: AppScope,
  enterpriseService: EnterpriseService
): RemoteData<ResolvedAppScope> {
  const { organizationId } = slugScope;

  const workspaceRd = enterpriseService.useWorkspaceBySlug(
    slugScope.level !== 'organization'
      ? maybe.of(organizationId)
      : maybe.ofAbsent(),
    slugScope.level === 'workspace' || slugScope.level === 'team'
      ? maybe.of(slugScope.workspaceId)
      : maybe.ofAbsent()
  );

  const teamRd = enterpriseService.useTeamBySlug(
    slugScope.level === 'team' ? maybe.of(organizationId) : maybe.ofAbsent(),
    slugScope.level === 'team'
      ? maybe.of(slugScope.workspaceId)
      : maybe.ofAbsent(),
    slugScope.level === 'team' ? maybe.of(slugScope.teamId) : maybe.ofAbsent()
  );

  if (slugScope.level === 'organization') {
    return rd.of({ level: 'organization', organizationId });
  }

  if (slugScope.level === 'workspace') {
    return resolveWorkspaceScope(
      organizationId,
      slugScope.workspaceId,
      workspaceRd
    );
  }

  return resolveTeamScope(
    organizationId,
    slugScope.workspaceId,
    slugScope.teamId,
    workspaceRd,
    teamRd
  );
}
